/**
 * Hero Carousel — dark navy slides with auto-rotation.
 * Used on LandingPage (logged-out) and DashboardHome (logged-in).
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline Swiss map SVG (decorative, used as fallback when no image_url)
const SwissMapBg = () => (
  <svg viewBox="0 0 400 240" className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="xMidYMid slice" role="presentation" aria-hidden="true">
    <defs>
      <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
      </radialGradient>
    </defs>
    {[
      [80, 120], [110, 100], [140, 90], [170, 95], [200, 110], [230, 105],
      [260, 120], [285, 130], [310, 140], [120, 140], [150, 150], [180, 160],
      [210, 170], [240, 180], [270, 175], [100, 165], [140, 180], [200, 195],
      [260, 200], [320, 160], [340, 130]
    ].map((p, i) => (
      <g key={i}>
        <circle cx={p[0]} cy={p[1]} r="8" fill="url(#dotGlow)" />
        <circle cx={p[0]} cy={p[1]} r="2" fill="#60a5fa" />
      </g>
    ))}
    {[
      [80, 120, 110, 100], [110, 100, 140, 90], [140, 90, 170, 95],
      [170, 95, 200, 110], [200, 110, 230, 105], [120, 140, 150, 150],
      [150, 150, 180, 160], [180, 160, 210, 170], [210, 170, 200, 110],
      [240, 180, 260, 120], [260, 200, 285, 130]
    ].map((l, i) => (
      <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]}
            stroke="#3b82f6" strokeOpacity="0.35" strokeWidth="0.5" />
    ))}
  </svg>
);

export const HeroCarousel = ({ language = "en" }) => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hero-slides`).then(r => setSlides(r.data.slides || [])).catch(() => {});
  }, []);

  useEffect(() => {
    // Auto-rotation disabled — slides only change when user clicks the dots
    // (Previously changed every 6 seconds which felt distracting on the home page)
    return undefined;
  }, [slides.length]);

  if (slides.length === 0) return null;
  const s = slides[idx];
  const title = s[`title_${language}`] || s.title_en;
  const subtitle = s[`subtitle_${language}`] || s.subtitle_en;
  const ctaLabel = s[`cta_label_${language}`] || s.cta_label_en || "Learn more";

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
      style={{
        background: "linear-gradient(135deg, #0A1A3E 0%, #0F2557 100%)",
        minHeight: 220,
      }}
      data-testid="hero-carousel"
    >
      {s.image_url ? (
        <img src={s.image_url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" decoding="async" />
      ) : (
        <SwissMapBg />
      )}
      <div aria-hidden="true" className="absolute inset-0" style={{
        background: "linear-gradient(90deg, rgba(10,26,62,0.95) 0%, rgba(10,26,62,0.55) 60%, rgba(10,26,62,0.2) 100%)"
      }} />

      <div className="relative p-6 sm:p-8 flex flex-col h-full min-h-[220px]">
        <div className="max-w-md">
          <h2 className="text-white text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight mb-3"
              style={{ fontFamily: "Chivo, sans-serif" }}>
            {(title || "").split(".").map((part, i, arr) => (
              <span key={i}>
                <span className={i === arr.length - 2 ? "text-[#60a5fa]" : ""}>{part}</span>
                {i < arr.length - 1 && <>.<br/></>}
              </span>
            ))}
          </h2>
          <p className="text-blue-100/80 text-sm sm:text-base mb-5 leading-relaxed">{subtitle}</p>
          <button
            onClick={() => navigate(s.cta_url || "/register")}
            className="inline-flex items-center gap-2 bg-[#0044CC] hover:bg-[#0055EE] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg hover:shadow-blue-500/30"
            data-testid="hero-cta"
          >
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Dots — WCAG 2.5.5 compliant tap targets (44×44 hit area with visible dot inside) */}
        <div className="absolute bottom-3 left-4 sm:left-8 flex gap-1" role="tablist" aria-label="Hero slides">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Show slide ${i + 1} of ${slides.length}`}
              className="p-2.5 -m-1 group focus-visible:outline-white"
            >
              <span
                aria-hidden="true"
                className={`block h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#60a5fa]" : "w-1.5 bg-white/50 group-hover:bg-white/80"}`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
